import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {UIHandlers} from '@api/model/ui-handlers'
import {
  addingAllowed,
  deleteAllowed,
  renameAllowed
} from '@client/hub-actions-allowed'
import {ColumnChooser} from '@components/column-chooser'
import {ColumnColorPicker} from '@components/column-color-picker/column-color-picker'
import {IconButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {crmStore} from '@components/crm/crm-store'
import {useBoard, useProject} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {Sticker} from '@extensions/sticker/sticker'
import {UserAccess} from '@extensions/user-access'
import {ColumnStore, useColumnStore} from '../column-store'

type ColumnMenuVisibleItem =
  | 'rename'
  | 'archive-all'
  | 'archive-completed'
  | 'move'
  | 'duplicate'
  | 'sort'
  | 'ui-handlers'
  | 'colorpicker'
  | 'delete'

type ColumnMenuSortVisibleItem =
  | 'deadline'
  | 'executor'
  | 'create-date'
  | 'completed'
  | 'task-name'
  | 'time-in-column'

// eslint-disable-next-line eslint-comments/no-restricted-disable
// eslint-disable-next-line max-lines-per-function
const getSortMenu = (props: {
  store: ColumnStore
  group: Hub
  showTimeInColumn: boolean | undefined
  visibleItems?: ColumnMenuSortVisibleItem[]
}): MenuItemData[] => {
  const {store, group, showTimeInColumn, visibleItems} = props

  const sortItems: MenuItemData[] = []
  const isVisible = (name: ColumnMenuSortVisibleItem) =>
    !visibleItems || visibleItems.includes(name)

  if (isVisible('deadline')) {
    sortItems.push({
      id: 'deadline',
      icon: 'IconCustomCalendar',
      title: Lang.msg.columnOrder.orderByDeadline,
      items: [
        {
          id: 'asc',
          icon: 'IconArrowUp',
          title: Lang.msg.columnOrder.orderByOld,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'deadline',
              action: 'old'
            })
        },
        {
          id: 'desc',
          icon: 'IconArrowDown',
          title: Lang.msg.columnOrder.orderByNew,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'deadline',
              action: 'new'
            })
        }
      ]
    })
  }

  if (isVisible('executor')) {
    sortItems.push({
      id: 'executor',
      icon: 'user',
      title: Lang.msg.columnOrder.orderByExecutor,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAZ',
          title: Lang.msg.columnOrder.orderByAZ,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'executor',
              action: 'az'
            })
        },
        {
          id: 'desc',
          icon: 'IconSortZA',
          title: Lang.msg.columnOrder.orderByZA,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'executor',
              action: 'za'
            })
        }
      ]
    })
  }

  if (isVisible('create-date')) {
    sortItems.push({
      id: 'create-date',
      icon: 'IconCalendar',
      title: Lang.msg.columnOrder.orderByDateCreation,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAscending2',
          title: Lang.msg.columnOrder.orderByOld,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'dateCreation',
              action: 'old'
            })
        },
        {
          id: 'desc',
          icon: 'IconSortDescending2',
          title: Lang.msg.columnOrder.orderByNew,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'dateCreation',
              action: 'new'
            })
        }
      ]
    })
  }

  if (isVisible('completed')) {
    sortItems.push({
      id: 'completed',
      icon: 'IconCircleCheck',
      title: Lang.msg.columnOrder.orderByDone,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAscending2',
          title: Lang.msg.columnOrder.orderFirstCompleted,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'done',
              action: 'completed'
            })
        },
        {
          id: 'desc',
          icon: 'IconSortDescending2',
          title: Lang.msg.columnOrder.orderFirstUnfulfilled,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'done',
              action: 'unfulfilled'
            })
        }
      ]
    })
  }

  if (isVisible('task-name')) {
    sortItems.push({
      id: 'task-name',
      icon: 'IconBallpen',
      title: Lang.msg.columnOrder.orderByTaskName,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAZ',
          title: Lang.msg.columnOrder.orderByAZ,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'taskName',
              action: 'az'
            })
        },
        {
          id: 'desc',
          icon: 'IconSortZA',
          title: Lang.msg.columnOrder.orderByZA,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'taskName',
              action: 'za'
            })
        }
      ]
    })
  }

  store.getCustomStickers().forEach(sticker => {
    const icon = Sticker.getIcon(sticker)
    const stickerType = Sticker.getType(sticker)
    const subMenuType =
      stickerType === 'text' ? 'textCustomSticker' : 'customSticker'

    sortItems.push({
      id: sticker.id,
      icon: (
        <Icon
          icon={icon || 'IconCustomBan'}
          color={icon ? 'sticker-gray' : 'placeholder'}
        />
      ),
      title: sticker.title,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAscending2',
          title: Lang.msg.columnOrder.orderByAsc,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: subMenuType,
              action: 'az',
              stickerId: sticker.id,
              stickerType
            })
        },
        {
          id: 'desc',
          icon: 'IconSortDescending2',
          title: Lang.msg.columnOrder.orderByDesc,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: subMenuType,
              action: 'za',
              stickerId: sticker.id,
              stickerType
            })
        }
      ]
    })
  })

  showTimeInColumn &&
    isVisible('time-in-column') &&
    sortItems.push({
      id: 'time-in-column',
      icon: 'IconCustomTimeInColumn',
      title: Lang.msg.columnOrder.orderByTimeInColumn,
      items: [
        {
          id: 'asc',
          icon: 'IconSortAZ',
          title: Lang.msg.columnOrder.orderByOld,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'timeInColumn',
              action: 'old'
            })
        },
        {
          id: 'desc',
          icon: 'IconSortZA',
          title: Lang.msg.columnOrder.orderByNew,
          onClick: () =>
            store.setNewOrder({
              column: group,
              subMenu: 'timeInColumn',
              action: 'new'
            })
        }
      ]
    })

  return sortItems
}

const useColumnMenu = (group: Hub, visible: boolean): MenuItemData[] => {
  const [state, store] = useColumnStore()
  const board = useBoard()
  const project = useProject()
  const userId = globals.currentUserId
  const currentRole = UserAccess.getRoleInProject(userId, project)
  const showTimeInColumn = board.data.settings?.showTimeTaskInColumn
  const isMirror = Column.isMirror(group)

  const isCrmBoard = crmStore.isCrmBoard(board)
  const isMirrorFromCrm =
    isMirror && crmStore.isCrmLocation(store.getVisibleColumnId())

  let visibleItems: ColumnMenuVisibleItem[]
  let sortMenuVisibleItems: ColumnMenuSortVisibleItem[]

  const isVisible = (name: ColumnMenuVisibleItem) =>
    !visibleItems || visibleItems.includes(name)

  if (isCrmBoard || isMirrorFromCrm) {
    visibleItems = ['rename', 'sort', 'colorpicker']

    if (
      isMirror ||
      (!crmStore.isSucceededColumn(group) && !crmStore.isRejectedColumn(group))
    ) {
      visibleItems.push('delete')
    }

    sortMenuVisibleItems = [
      'create-date',
      'executor',
      'task-name',
      'time-in-column'
    ]
  }

  return useMemo(() => {
    const items: MenuItemData[] = []

    if (!visible) {
      return items
    }

    if (isVisible('rename')) {
      items.push({
        id: 'rename',
        icon: 'IconEdit',
        title: Lang.msg.rename,
        onClick: store.groupStartEditOrShowWarn,
        disabled: !renameAllowed(group.id)
      })
    }

    if (isVisible('archive-all')) {
      items.push({
        id: 'archive-all',
        icon: 'IconArchive',
        title: Lang.msg.menuArchiveTasks,
        onClick: () => store.archiveTasks(false)
      })
    }

    if (isVisible('archive-completed')) {
      items.push({
        id: 'archive-completed',
        icon: 'IconArchive',
        title: Lang.msg.menuArchiveCompleted,
        onClick: () => store.archiveTasks(true)
      })
    }

    if (isVisible('move')) {
      items.push({
        id: 'move',
        icon: 'IconCornerUpRight',
        title: Lang.msg.move,
        onClick: store.moveOpen,
        disabled: !globals.dragAllowed(group.id)
      })
    }

    if (isVisible('duplicate')) {
      items.push({
        id: 'duplicate',
        icon: 'IconCopy',
        title: Lang.msg.duplicate,
        onClick: store.duplicate,
        disabled: !addingAllowed(group.location) || state.isMirror
      })
    }

    if (isVisible('sort')) {
      items.push({
        id: 'sort',
        icon: 'IconArrowsSort',
        title: Lang.context.column.orderTasksBy,
        items: getSortMenu({
          store,
          group,
          showTimeInColumn,
          visibleItems: sortMenuVisibleItems
        }),
        disabled: !store.columnSortAllowed() || state.isMirror
      })
    }

    if (isVisible('ui-handlers')) {
      for (const el of UIHandlers.columnMenuShow(group)) {
        items.push({
          id: el.title,
          title: el.title,
          onClick: el.onClick,
          icon: (el as any).icon ?? 'IconArchive'
        })
      }
    }

    if (isVisible('colorpicker')) {
      if (renameAllowed(group.id) && !isMirror) {
        items.push(
          {separator: true},
          {
            id: 'colorpicker',
            element: <ColumnColorPicker columnId={group.id} />
          }
        )
      }
    }

    if (isVisible('delete')) {
      items.push(
        {separator: true},
        {
          id: 'delete',
          icon: 'IconTrash',
          color: 'error',
          title: Lang.msg.delete,
          onClick: store.delete,
          disabled: !deleteAllowed(group.id)
        }
      )
    }

    return items
  }, [
    showTimeInColumn,
    group.id,
    isMirror,
    state.isMirror,
    store,
    state,
    group.location,
    currentRole,
    visible
  ])
}

export const ColumnMenu = observer(({className}: {className?: string}) => {
  const [state, store] = useColumnStore()
  const group = store.column

  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  const open = useCallback(() => setVisible(true), [setVisible])
  const close = useCallback(() => setVisible(false), [setVisible])

  const menuItems = useColumnMenu(group, visible)

  return (
    <>
      <IconButton
        ref={ref}
        icon="IconDotsVertical"
        color="grey"
        size="small"
        wrapperSize="big"
        className={cn('relative', className)}
        forcedState={visible ? 'active' : undefined}
        onClick={open}
      />
      {visible && (
        <Dropdown
          reference={ref}
          hideArrow
          className="overflow-visible"
          placement="bottom-start"
          fallbackPlacements={['bottom-end', 'right-start', 'left-start']}
          content={<Menu onItemClicked={close} items={menuItems} />}
          visible
          onVisibleChange={close}
          theme="new-menu"
        />
      )}
      {state.moving && (
        <Dropdown
          reference={ref}
          hideArrow
          className="overflow-visible"
          placement="bottom-start"
          fallbackPlacements={['bottom-end', 'right-start', 'left-start']}
          content={
            <ColumnChooser
              onChoose={store.onMoveTargetChoose}
              chooseBoards
              singleColumn
              className="!mb-0"
            />
          }
          visible
          onVisibleChange={store.moveClose}
        />
      )}
    </>
  )
})
